// src/components/bot/hooks/useTemplateManagement.ts
// 🧠 TEMPLATE MANAGEMENT LOGIC - Pure business logic

import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { getTemplateById } from '../../../services/templates';
import logger from '../../../utils/logger';

// 🎯 INTERFACES
interface TemplatePreviewData {
  bodyValue: string;
  values: {
    header: string;
    header_value: string;
    footer?: string;
    haveButtons: "yes" | "no";
    body?: string;
    buttons?: Array<{
      type: string;
      text: string;
      url?: string;
      phone_number?: string;
    }>;
  };
  file: File | null;
}

interface UseTemplateProps {
  selectedTemplate: any;
  onTemplateChange: (template: any) => void;
}

// 🧠 TEMPLATE MANAGEMENT HOOK
export const useTemplateManagement = ({ selectedTemplate, onTemplateChange }: UseTemplateProps) => {
  
  // 🎯 TEMPLATE STATE
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState<TemplatePreviewData | null>(null);
  const [templateCache, setTemplateCache] = useState<Record<string, any>>({});
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  // 🔥 TRANSFORM TEMPLATE: Convert template data for preview
  const transformTemplateForPreview = useCallback((template: any): TemplatePreviewData => {
    const components = template.components || [];
    
    if (components.length === 0) {
      return {
        bodyValue: `Preview for template: ${template.name}`,
        values: {
          header: 'none',
          header_value: '',
          footer: 'Template preview loading...',
          haveButtons: "no",
          body: `Template: ${template.name}`,
          buttons: []
        },
        file: null
      };
    }
    
    const headerComponent = components.find((c: any) => c.type === 'HEADER');
    const bodyComponent = components.find((c: any) => c.type === 'BODY');
    const footerComponent = components.find((c: any) => c.type === 'FOOTER');
    const buttonsComponent = components.find((c: any) => c.type === 'BUTTONS');

    let bodyText = bodyComponent?.text || '';
    const sampleValues = ['John Doe', 'ABC123', '15 minutes', '$99.99', 'Premium Plan'];
    bodyText = bodyText.replace(/\{\{(\d+)\}\}/g, (match: string, number: string) => {
      const index = parseInt(number) - 1;
      return sampleValues[index] || `Sample ${number}`;
    });

    return {
      bodyValue: bodyText,
      values: {
        header: headerComponent?.format?.toLowerCase() || 'none',
        header_value: headerComponent?.text || '',
        footer: footerComponent?.text || '',
        haveButtons: (buttonsComponent?.buttons || []).length > 0 ? "yes" : "no",
        body: bodyText,
        buttons: buttonsComponent?.buttons || []
      },
      file: null
    };
  }, []);

  // 🔥 FETCH TEMPLATE: Load and cache template data
  const fetchTemplateForPreview = useCallback(async (templateId: string) => {
    if (templateCache[templateId]) {
      logger.log('🔥 useTemplate: Using cached template');
      const previewData = transformTemplateForPreview(templateCache[templateId]);
      setTemplatePreviewData(previewData);
      return;
    }

    if (templateLoading) return;

    setTemplateLoading(true);
    try {
      const response = await getTemplateById(templateId);
      const templateData = response.data.data || response.data;
      
      if (templateData) {
        // 🎯 CACHE TEMPLATE: Store for future use
        setTemplateCache(prev => ({ ...prev, [templateId]: templateData }));
        
        // 🎯 UPDATE SELECTED: Enhance with template name
        if (selectedTemplate && selectedTemplate._id === templateId) {
          onTemplateChange({
            ...selectedTemplate,
            name: templateData.name
          });
        }

        // 🎯 GENERATE PREVIEW: Transform for UI
        const previewData = transformTemplateForPreview(templateData);
        setTemplatePreviewData(previewData);
      }
    } catch (error) {
      logger.error('❌ useTemplate: Template fetch error:', error);
      toast.error('Failed to load template preview');
    } finally {
      setTemplateLoading(false);
    }
  }, [templateCache, templateLoading, transformTemplateForPreview, selectedTemplate, onTemplateChange]);

  // 🔥 SELECT TEMPLATE: Handle template selection
  const handleTemplateSelect = useCallback((template: any) => {
    logger.log('🎯 useTemplate: Template selected:', template.name);
    
    onTemplateChange(template);
    setShowTemplateSelector(false);
    
    // 🎯 FETCH PREVIEW: Load template data for preview
    if (template._id) {
      fetchTemplateForPreview(template._id);
    }
  }, [onTemplateChange, fetchTemplateForPreview]);

  // 🔥 OPEN SELECTOR: Show template selection modal
  const openTemplateSelector = useCallback(() => {
    logger.log('📋 useTemplate: Opening template selector');
    setShowTemplateSelector(true);
  }, []);

  // 🔥 CLOSE SELECTOR: Hide template selection modal
  const closeTemplateSelector = useCallback(() => {
    logger.log('❌ useTemplate: Closing template selector');
    setShowTemplateSelector(false);
  }, []);

  // 🔥 CLEAR TEMPLATE: Remove selected template
  const clearTemplate = useCallback(() => {
    logger.log('🗑️ useTemplate: Clearing template');
    onTemplateChange(null);
    setTemplatePreviewData(null);
  }, [onTemplateChange]);

  // 🔥 DETECT PERSONALIZATION: Check for personalization fields
  const detectPersonalizationFields = useCallback((template: any) => {
    logger.log('🔍 useTemplate: Detecting personalization fields in template:', template?.name);
    
    if (!template || !template.components) {
      logger.log('❌ No template or components found');
      return { hasFields: false, fields: [] };
    }

    // Find body component
    const bodyComponent = template.components.find((c: any) => c.type === 'BODY');
    if (!bodyComponent || !bodyComponent.text) {
      logger.log('❌ No body component or text found');
      return { hasFields: false, fields: [] };
    }

    logger.log('📝 Template body text:', bodyComponent.text);

    // Regex to find {{1}}, {{2}}, {{variable}}, etc.
    const personalizationRegex = /\{\{([^}]+)\}\}/g;
    const matches: string[] = bodyComponent.text.match(personalizationRegex) || [];
    
    // Remove duplicates and clean up
    const uniqueFields: string[] = [...new Set(matches)];
    
    logger.log('🎯 Personalization fields detected:', uniqueFields);
    
    return {
      hasFields: uniqueFields.length > 0,
      fields: uniqueFields
    };
  }, []);

  // 🔥 TEMPLATE VALIDATION: Check if template is valid
  const validateTemplate = useCallback(() => {
    if (!selectedTemplate) {
      return { isValid: false, error: 'Please select a template' };
    }
    
    return { isValid: true, error: null };
  }, [selectedTemplate]);

  // 🔥 RETURN: Clean interface for UI components
  return {
    // State
    selectedTemplate,
    templateLoading,
    templatePreviewData,
    showTemplateSelector,
    
    // Actions
    handleTemplateSelect,
    openTemplateSelector,
    closeTemplateSelector,
    clearTemplate,
    fetchTemplateForPreview,
    
    // Utilities
    detectPersonalizationFields,
    validateTemplate,
    transformTemplateForPreview,
    
    // Cache
    templateCache
  };
};